import throttle from 'lodash/throttle'

import Vue from 'vue'
import { Mixin } from 'vue-mixin-decorator'
import { Prop, Watch } from 'vue-property-decorator'

type EventHandler = (e: TouchEvent) => void

interface EventFreeze {
  onStart: EventHandler
  onMove: EventHandler
}

@Mixin
export default class LockBackground extends Vue {
  originalOverflowOfBody = ''
  originalOverflowOfHtml = ''

  @Prop({ default: true })
  lock: boolean

  @Prop({ default: false })
  keep: boolean

  get scrollable(): HTMLElement {
    // hook method. override prevent touch move on specify class dom.
    // default return null
    return this.$el as HTMLElement
  }

  mounted() {
    if (this.lock) {
      this.doLock()
    }
  }
  beforeDestroy() {
    if (this.keep) {
      return
    }

    this.doUnlock()
  }

  @Watch('lock')
  onChangeLock() {
    this.lock ? this.doLock() : this.doUnlock()
  }

  private get freezeBackground(): EventFreeze {
    const store = {
      onTopEnd: true,
      onBottomEnd: true,
      touch: {
        prevY: 0,
      },
    }

    const onStart = (e: TouchEvent) => {
      const target = this.scrollable

      const currentTop = target.scrollTop

      store.onTopEnd = currentTop <= 0
      store.onBottomEnd = currentTop >= target.scrollHeight - target.offsetHeight

      store.touch.prevY = e.targetTouches[0].pageY
    }
    const onMove = (e: TouchEvent) => {
      const currentY = e.targetTouches[0].pageY
      const prevY = store.touch.prevY
      store.touch.prevY = currentY

      const toUp = currentY > prevY
      const toDown = currentY < prevY

      if (toUp && store.onTopEnd) {
        e.preventDefault()

        return
      }
      if (toDown && store.onBottomEnd) {
        e.preventDefault()

        return
      }
    }

    // Vue.jsのcomputedは、依存するリアクティブプロパティが更新されない限り、2回目はキャッシュを返す.
    return {
      onStart,
      onMove: throttle(onMove),
    }
  }

  private doLock() {
    /**
     * html, bodyのクラスを変更するとDOMの再計算が行われてINPが悪くなるため、スタイルを直接変更する
     * @see https://github.com/Everforth/growthpalette-front/issues/7839
     *
     * 複数のLockBackgroundを使用した場合に、
     * それらのdoLockが実行された逆順にdoUnlockが実行されるとは限らない。
     * 最後に実行するdoUnlockでoverflow属性をhiddenにしてしまうと
     * ユーザーはスクロールできなくなってしまうため、それを防ぐために
     * originalOverflowOfXXXにhiddenの値は設定しない。
     *
     * FIXME: 現在の方法だと、LockBackgroundを用いる複数のモーダルを開いた場合、
     *        最初にモーダルを閉じた時点で、再度ページ全体のスクロールできるようになってしまう。
     */
    if (document.body.style.overflow !== 'hidden') {
      /**
       * デフォルト値としては、スタイル宣言のリセットを表す空文字列を設定する
       * https://developer.mozilla.org/ja/docs/Web/API/HTMLElement/style
       */
      this.originalOverflowOfBody = document.body.style.overflow || ''
      document.body.style.overflow = 'hidden'
    }

    const htmlTag = document.getElementsByTagName('html')[0]
    if (htmlTag.style.overflow !== 'hidden' && htmlTag) {
      this.originalOverflowOfHtml = htmlTag.style.overflow || ''
      htmlTag.style.overflow = 'hidden'
    }

    const { onStart, onMove } = this.freezeBackground

    // https://www.imuza.com/entry/2018/06/12/172357
    // iOS + Safariで、背景のバウンスを防ぎ、コンテンツのスクロールが阻害されないようにする.
    document.body.addEventListener('touchstart', onStart)
    document.body.addEventListener('touchmove', onMove, { passive: false })
  }
  private doUnlock() {
    document.body.style.overflow = this.originalOverflowOfBody

    const htmlTag = document.getElementsByTagName('html')[0]
    if (htmlTag) {
      htmlTag.style.overflow = this.originalOverflowOfHtml
    }

    const { onStart, onMove } = this.freezeBackground

    // iOS + Safari対策の後始末
    document.body.removeEventListener('touchstart', onStart)
    document.body.removeEventListener('touchmove', onMove)
  }
}
